import { __awaiter } from "tslib";
/* eslint-disable no-shadow */
import get from 'lodash/get';
import has from 'lodash/has';
import reduce from 'lodash/reduce';
import merge from 'lodash/merge';
import { deepMergeObjects } from '../objects/objects';
import { initialState, setData, setError, setStatus, clearData, resetState, } from './state';
import { FALLBACK_MESSAGE } from '../const/message.const';
import { STORE_REQUEST_STATUS } from '../const/store.const';
import { sanitizeError } from '../sanitize/sanitize';
const getFormattedError = (error) => {
    const errorPathMessage = has(error, 'data.message') ? 'data.message' : '[0].message';
    const exceptionMessage = get(error, 'data.exception_message') || get(error, 'data.exceptionMessage') || get(error, 'response.data.exception_message') || get(error, 'response.data.exceptionMessage');
    const errorMessage = get(error, 'data.error') || get(error, 'exception_message') || get(error, 'response.data.message') || FALLBACK_MESSAGE.error;
    return {
        message: get(error, errorPathMessage) || errorMessage,
        key: get(error, 'data.key', null) || get(error, 'response.data.key', null),
        custom_data: get(error, 'data.custom_data', null),
        customData: get(error, 'data.custom_data', null),
        data: get(error, 'data.data', null),
        status: get(error, 'status', 0),
        exceptionMessage,
    };
};
/**
 * Runs a function that contains an API request and populates the either
 * the vuex state or a specific scope in the state. The populated structure
 * follows the form described in [[initialState]].
 * @param commit {function} Actual vuex commit function needs to be injected
 *                          in order to execute the migrations.
 * @param storeConfig {object} Object contaning the store structure
 * @returns object
 */
export const runAsyncFlow = (commit, { scope, request, params = [] }) => __awaiter(void 0, void 0, void 0, function* () {
    const scoped = (value) => {
        return scope ? { scope, value } : value;
    };
    try {
        commit('setStatus', scoped(STORE_REQUEST_STATUS.loading));
        const response = yield request(...params);
        commit('setData', scoped(response));
        commit('setStatus', scoped(STORE_REQUEST_STATUS.loaded));
        return response;
    }
    catch (error) {
        const formattedError = getFormattedError(error);
        const sanitizedError = sanitizeError(formattedError);
        commit('setError', scoped(sanitizedError));
        commit('setStatus', scoped(STORE_REQUEST_STATUS.error));
    }
});
/**
 * Adds and setups scopes to an existing store object,
 * so each scope has its own [[initialState]] structure.
 * Each scope will become its own object hanging from `state`.
 * @param scopes {object} Object containing key/value pairs, where value will become the actual scope name.
 * @param storeConfig {object} Object contaning the store structure
 * @param isBufferSupported {boolean} Information about support for Buffer
 * @returns Dictionary<unknown>
 */
export const withScopes = (scopes, storeConfig, isBufferSupported = false) => {
    const storeScopes = {
        state: reduce(scopes, (accumulator, value) => {
            return Object.assign(Object.assign({}, accumulator), { [value]: Object.assign({}, initialState) });
        }, {}),
    };
    return isBufferSupported
        ? merge(storeScopes, storeConfig)
        : deepMergeObjects(storeScopes, storeConfig);
};
/**
 * Creates a store with a predefined mutations,
 * namespace as true and state initialised.
 * See also:
 * [[initialState]], [[setStatus]], [[setData]], [[setError]], [[runAsyncFlow]], [[clearData]], [[resetState]]
 * @param getCustomConfig {({ runAsyncFlow } : { runAsyncFlow: RunAsyncFlowFunction }) => object}
 * @param isBufferSupported {boolean} Information about support for Buffer
 * @returns {Dictionary<unknown>} Object contaning the store structure
 */
export const createCustomStore = (getCustomConfig, isBufferSupported = true) => {
    const config = getCustomConfig({ runAsyncFlow });
    const storeBoilerplate = {
        namespaced: true,
        state: Object.assign({}, initialState),
        mutations: {
            setStatus,
            setData,
            setError,
            clearData,
            resetState,
        },
    };
    return isBufferSupported
        ? merge(storeBoilerplate, config)
        : deepMergeObjects(storeBoilerplate, config);
};
